/**
 * ProtocolsListView - List view for the protocols management modal.
 * Displays all saved protocols with edit/remove actions on hover.
 */

import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import {
  DialogClose,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@heidi/design-system/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import {
  TypographyFootNoteBold,
  typographyVariant,
} from '@heidi/design-system/typography';
import { Library, Pencil, Plus, SlashSquare, Trash2, X } from 'lucide-react';
import { memo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { protocolsListViewMessages } from './protocols-list-view.messages';
import type { PinnedProtocol } from './protocols-management-modal';

const getRemoveTooltipMessage = (protocol: PinnedProtocol) => {
  if (protocol.teamShortcutId) return protocolsListViewMessages.dismissTeamShortcut;
  if (protocol.isDefault) return protocolsListViewMessages.removeFromMyShortcuts;
  return protocolsListViewMessages.removeShortcut;
};

export interface ProtocolsListViewProps {
  protocols: PinnedProtocol[];
  showProBranding: boolean;
  onOpenCreate: () => void;
  onOpenEdit: (protocol: PinnedProtocol) => void;
  onRemoveProtocol: (chipId: string) => void;
  onBrowseLibrary?: () => void;
  shortcutCount: number;
  maxShortcuts: number;
  isAtShortcutLimit: boolean;
}

export const ProtocolsListView = memo(
  ({
    protocols,
    showProBranding,
    onOpenCreate,
    onOpenEdit,
    onRemoveProtocol,
    onBrowseLibrary,
    shortcutCount,
    maxShortcuts,
    isAtShortcutLimit,
  }: ProtocolsListViewProps) => {
    const intl = useIntl();
    return (
      <>
        {/* List View Header */}
        <DialogHeader className="space-y-1">
          <div className="flex items-start justify-between">
            <DialogTitle>
              <FormattedMessage {...protocolsListViewMessages.savedShortcuts} />
            </DialogTitle>
            <div className="flex items-center gap-1">
              <Tooltip delayDuration={300}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    onClick={onOpenCreate}
                    disabled={isAtShortcutLimit}
                    aria-label={intl.formatMessage(
                      protocolsListViewMessages.addShortcutLower
                    )}
                  >
                    <Plus size={18} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" sideOffset={4}>
                  {isAtShortcutLimit
                    ? intl.formatMessage(
                        protocolsListViewMessages.savedShortcutLimitReached,
                        {
                          max: maxShortcuts,
                        }
                      )
                    : intl.formatMessage(protocolsListViewMessages.addShortcutLower)}
                </TooltipContent>
              </Tooltip>
              {onBrowseLibrary && (
                <Tooltip delayDuration={300}>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconOnly
                      onClick={onBrowseLibrary}
                      aria-label={intl.formatMessage(
                        protocolsListViewMessages.seeAllShortcuts
                      )}
                    >
                      <Library size={18} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" sideOffset={4}>
                    {intl.formatMessage(protocolsListViewMessages.seeAllShortcuts)}
                  </TooltipContent>
                </Tooltip>
              )}
              <DialogClose asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  aria-label={intl.formatMessage(protocolsListViewMessages.closeAria)}
                >
                  <X size={18} />
                </Button>
              </DialogClose>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <DialogDescription className="text-text-tertiary">
              <FormattedMessage
                {...protocolsListViewMessages.savedShortcutsDescription}
              />
            </DialogDescription>
            <span
              className={cn(
                'shrink-0',
                typographyVariant({ variant: 'caption' }),
                'text-text-tertiary'
              )}
            >
              {intl.formatMessage(protocolsListViewMessages.savedShortcutQuota, {
                count: shortcutCount,
                max: maxShortcuts,
              })}
            </span>
          </div>
        </DialogHeader>

        {/* Scrollable protocols list */}
        <div className="-mx-6 max-h-96 flex-1 overflow-y-auto px-6">
          {protocols.length > 0 ? (
            <div className="flex flex-col gap-2">
              {protocols.map((protocol) => (
                <div
                  key={protocol.id}
                  className="group/card relative flex flex-col rounded-lg border border-border bg-surface p-3"
                >
                  <div className="flex min-w-0 flex-1 items-start gap-2">
                    <SlashSquare
                      size={14}
                      className={cn(
                        'mt-0.5 shrink-0',
                        showProBranding ? 'text-tier-pro-text' : 'text-text-secondary'
                      )}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                        <span className="line-clamp-2 text-sm font-medium text-text-primary">
                          {protocol.label}
                        </span>
                        {protocol.teamShortcutId && (
                          <TypographyFootNoteBold className="inline-flex shrink-0 items-center rounded-full bg-surface-accent px-1.5 py-0.5 uppercase tracking-wide text-text-secondary">
                            <FormattedMessage {...protocolsListViewMessages.teamBadge} />
                          </TypographyFootNoteBold>
                        )}
                      </div>
                      {protocol.query && (
                        <p className="mt-1 line-clamp-2 text-xs text-text-secondary">
                          {protocol.query}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="absolute end-2 top-2 flex items-center gap-1 opacity-0 transition-opacity duration-150 group-hover/card:opacity-100">
                    {!protocol.isDefault && !protocol.teamShortcutId && (
                      <Tooltip delayDuration={300}>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="xs"
                            iconOnly
                            onClick={() => onOpenEdit(protocol)}
                            aria-label={intl.formatMessage(
                              protocolsListViewMessages.editProtocolAria,
                              {
                                name: protocol.label,
                              }
                            )}
                          >
                            <Pencil size={12} className="text-text-secondary" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top" sideOffset={4}>
                          {intl.formatMessage(protocolsListViewMessages.editShortcut)}
                        </TooltipContent>
                      </Tooltip>
                    )}

                    <Tooltip delayDuration={300}>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="xs"
                          iconOnly
                          onClick={() => onRemoveProtocol(protocol.id)}
                          aria-label={intl.formatMessage(
                            protocolsListViewMessages.removeProtocolAria,
                            { name: protocol.label }
                          )}
                        >
                          <Trash2 size={12} className="text-text-secondary" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="top" sideOffset={4}>
                        {intl.formatMessage(getRemoveTooltipMessage(protocol))}
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <SlashSquare
                size={32}
                className={cn(
                  'mb-3',
                  showProBranding ? 'text-tier-pro-text' : 'text-text-tertiary'
                )}
              />
              <p className="mb-1 text-sm font-medium text-text-secondary">
                <FormattedMessage {...protocolsListViewMessages.noShortcutsYet} />
              </p>
              <p className="text-xs text-text-tertiary">
                <FormattedMessage {...protocolsListViewMessages.addFirstShortcut} />
              </p>
            </div>
          )}
        </div>
      </>
    );
  }
);

ProtocolsListView.displayName = 'ProtocolsListView';
